// [2026-09-02] ADDED — platform analytics, optionally scoped to one tenant.
//
// THE TENANT SELECTOR IS THE POINT OF THIS SCREEN. Overview answers "is the platform healthy";
// this answers "is THIS customer's integration healthy", which is the question a support
// conversation actually starts from. `client_id=` is reachable only here, behind require_admin.

import { useState } from 'react'
import {
  Card, Empty, Notice, Skeleton, Tile, num, ms, pct, useAsync,
} from '@incruiter/proctoring-client-frontend'
import {
  TrafficChart, LatencyTrend, LatencyHistogram, HourOfDayChart, Heatmap,
} from '@incruiter/proctoring-client-frontend'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@incruiter/proctoring-client-frontend/src/components/ui/table.jsx'
import { admin } from '../api.js'

export default function Analytics({ days = 30, onError }) {
  const [clientId, setClientId] = useState('')
  const { data: clientList } = useAsync(() => admin.clients(days), [days])
  const { data, error, loading } = useAsync(
    () => admin.analytics(days, clientId || undefined), [days, clientId])

  if (error) { onError?.(error); return <Notice kind="error">{error.message}</Notice> }

  const t = data?.totals || {}
  const lat = data?.latency || {}
  const p = lat.percentiles || {}

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 12.5 }}>Scope</span>
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="Tenant"
                style={{ padding: '9px 10px', borderRadius: 'var(--radius-sm)', minWidth: 260,
                         border: '1px solid var(--border)', background: 'var(--surface)',
                         color: 'inherit', font: 'inherit' }}>
          <option value="">Every tenant (platform-wide)</option>
          {(clientList?.clients || []).map((c) => (
            <option key={c.client_id} value={c.client_id}>
              {c.name || c.client_id} — {c.client_id}
            </option>
          ))}
        </select>
      </div>

      {loading || !data ? <Skeleton height={300} /> : (
        <>
          <div style={{ display: 'grid', gap: 12,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <Tile label="API calls" value={num(t.total_calls || 0)}
                  sub={`${t.active_days || 0} active days`} />
            <Tile label="Error rate" value={pct(t.error_rate || 0)}
                  tone={(t.error_rate || 0) > 0.05 ? 'var(--critical)' : undefined}
                  sub={`${num(t.error_calls || 0)} failed`} />
            <Tile label="Avg latency" value={t.avg_latency_ms == null ? '—' : ms(t.avg_latency_ms)} />
            <Tile label="P95" value={p.p95 == null ? '—' : ms(p.p95)}
                  sub={lat.approximate ? 'approx. (bucketed)' : undefined} />
            <Tile label="P99" value={p.p99 == null ? '—' : ms(p.p99)} />
            <Tile label="Credits spent" value={num(t.credits_spent || 0, { decimals: 2 })} />
          </div>

          <Card title="Traffic" subtitle={clientId ? `${clientId}, per day` : 'Every tenant, per day'}>
            <TrafficChart data={data.requests_series || []} />
          </Card>

          <div style={{ display: 'grid', gap: 16,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            <Card title="Latency trend" subtitle="Average and p95 per day">
              <LatencyTrend data={data.latency_series || []} />
            </Card>
            <Card title="Latency distribution"
                  subtitle={`${num(lat.sample_count || 0)} calls, bucketed`}>
              <LatencyHistogram distribution={lat.distribution || []} />
            </Card>
          </div>

          <div style={{ display: 'grid', gap: 16,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            <Card title="Hour of day" subtitle="UTC">
              <HourOfDayChart data={data.hour_of_day || []} />
            </Card>
            <Card title="Weekly pattern" subtitle="Weekday × hour, UTC">
              <Heatmap data={data.heatmap || []} />
            </Card>
          </div>

          <Card title="By endpoint" subtitle="Volume, failures and latency">
            {(data.endpoints || []).length === 0 ? (
              <Empty title="No API calls in this window" hint="" />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Endpoint</TableHead>
                      <TableHead style={{ textAlign: 'right' }}>Calls</TableHead>
                      <TableHead style={{ textAlign: 'right' }}>Client err</TableHead>
                      <TableHead style={{ textAlign: 'right' }}>Server err</TableHead>
                      <TableHead style={{ textAlign: 'right' }}>Error rate</TableHead>
                      <TableHead style={{ textAlign: 'right' }}>Avg latency</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.endpoints.map((row) => (
                      <TableRow key={row.endpoint}>
                        <TableCell className="mono">{row.endpoint}</TableCell>
                        <TableCell style={{ textAlign: 'right' }}>{num(row.total)}</TableCell>
                        <TableCell style={{ textAlign: 'right' }}>{num(row.client_error)}</TableCell>
                        <TableCell style={{ textAlign: 'right' }}>{num(row.server_error)}</TableCell>
                        <TableCell style={{ textAlign: 'right',
                                            color: row.error_rate > 0.05 ? 'var(--critical)' : undefined }}>
                          {pct(row.error_rate || 0)}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                          {row.avg_latency_ms == null ? '—' : ms(row.avg_latency_ms)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
